import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import styles from "./AddProduct.module.css";
import PageLoader from "../../Customs/PageLoader/PageLoader";
import { RootState } from "../../Services/Store";
import {
  WoodQuality,
  deleteWoodQuality,
  fetchWoodQualities,
  promptWoodQualityName,
  updateWoodQuality,
} from "../../Services/woodQualities";
import AddWoodQualityPanel from "./AddWoodQualityPanel";

interface InventoryAccess {
  write?: boolean;
  update?: boolean;
  delete?: boolean;
  [key: string]: any;
}

const WoodQualities: React.FC = () => {
  const navigate = useNavigate();
  const accessList = useSelector((state: RootState) => state.auth.access || []);
  const inventoryAccess: InventoryAccess =
    accessList.find((item: any) => item.code === "inventory_management") || {};
  const canWrite = !!inventoryAccess.write;
  const canUpdate = inventoryAccess.update !== false;
  const canDelete = !!inventoryAccess.delete;

  const [qualities, setQualities] = useState<WoodQuality[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const loadQualities = useCallback(async () => {
    setLoading(true);
    try {
      setQualities(await fetchWoodQualities());
    } catch {
      setQualities([]);
      toast.error("Failed to load wood qualities");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQualities();
  }, [loadQualities]);

  const handleAdd = () => {
    if (!canWrite) {
      toast.error("You do not have permission to add wood qualities.");
      return;
    }
    setShowAdd(true);
  };

  const handleEdit = async (quality: WoodQuality) => {
    if (!canUpdate) {
      toast.error("You do not have permission to update wood qualities.");
      return;
    }
    const name = await promptWoodQualityName("Rename wood quality", quality.name);
    if (!name || name === quality.name) return;
    try {
      const updated = await updateWoodQuality(quality.id, name);
      setQualities((prev) =>
        prev
          .map((q) => (q.id === quality.id ? updated : q))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      toast.success("Wood quality updated");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update wood quality");
    }
  };

  const handleDelete = async (quality: WoodQuality) => {
    if (!canDelete) {
      toast.error("You do not have permission to delete wood qualities.");
      return;
    }
    const confirm = await Swal.fire({
      title: "Delete wood quality",
      text: `Delete "${quality.name}"? It will no longer appear when adding a category or choosing a variant.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      heightAuto: false,
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteWoodQuality(quality.id);
      setQualities((prev) => prev.filter((q) => q.id !== quality.id));
      toast.success("Wood quality deleted");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete wood quality");
    }
  };

  return (
    <div className={styles.wrapper}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 7, marginBottom: 8 }}>
        <span className={styles.pageTitle}>Wood Qualities</span>
        <div className={styles.buttonRow}>
          <button className={styles.bigBtn} onClick={() => navigate("/inventory/product-design")}>
            Product Inventory
          </button>
          <button className={styles.bigBtn} onClick={() => navigate("/inventory/product-categories")}>
            Product Categories
          </button>
          <button className={`${styles.bigBtn} ${styles.active}`}>Wood Qualities</button>
        </div>
      </div>

      <div className={styles.toolbar}>
        <div />
        <button className={styles.addBtn} type="button" onClick={handleAdd} disabled={!canWrite}>
          <MdAdd style={{ marginRight: 6 }} /> Add Wood Quality
        </button>
      </div>

      <div className={styles.productTable}>
        {loading ? (
          <div style={{ padding: 34 }}>
            <PageLoader />
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ textAlign: "center" }}>Wood Quality</th>
                <th style={{ textAlign: "center" }} className={styles.actionsHeader}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {qualities.length === 0 ? (
                <tr>
                  <td colSpan={2} style={{ textAlign: "center", color: "#888", padding: "32px 0" }}>
                    No wood qualities found. Add a quality and set its price for each product category.
                  </td>
                </tr>
              ) : (
                qualities.map((quality) => (
                  <tr key={quality.id}>
                    <td style={{ textAlign: "center" }}>{quality.name}</td>
                    <td style={{ textAlign: "center" }} className={styles.actions}>
                      <FaEdit
                        className={styles.editIcon}
                        onClick={() => handleEdit(quality)}
                        style={{ cursor: canUpdate ? "pointer" : "not-allowed", opacity: canUpdate ? 1 : 0.4 }}
                        title="Edit"
                      />
                      <FaTrash
                        className={styles.deleteIcon}
                        onClick={() => handleDelete(quality)}
                        style={{ color: "#e53935", cursor: canDelete ? "pointer" : "not-allowed", opacity: canDelete ? 1 : 0.4 }}
                        title="Delete"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {showAdd && (
        <AddWoodQualityPanel
          onClose={() => setShowAdd(false)}
          onSuccess={() => {
            setShowAdd(false);
            loadQualities();
          }}
        />
      )}
    </div>
  );
};

export default WoodQualities;
